import { Link } from "@tanstack/react-router";
import { t } from "i18next";
import { Pencil, Trash2 } from "lucide-react";
import type { JSX } from "react";
import type { Template } from "@/common/types/templates";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useTemplateWrites } from "@/hooks/templates/useTemplateWrites";
import { toast } from "@/hooks/use-toast";
import { refreshTemplateReads } from "@/lib/template-queries";
import { getVisitScope } from "@/lib/visit-queries";

// Deletes a template with the toasts and list refresh the Templates page
// has always used; shared by the classic row and the redesigned one.
export const useTemplateDelete = (): {
  remove: (id: string) => Promise<void>;
  loading: boolean;
} => {
  const { remove, loading } = useTemplateWrites();
  const handleDeleteItem = async (id: string): Promise<void> => {
    const outcome = await remove(id);
    if (!outcome || getVisitScope() !== outcome.scope) return;
    const success = outcome.ok;
    toast({
      variant: success ? "default" : "error",
      title: t(
        success
          ? "templates.deletedToast.successful.title"
          : "templates.deletedToast.error.title",
      ),
      description: t(
        success
          ? "templates.deletedToast.successful.description"
          : "templates.deletedToast.error.description",
      ),
    });
    if (success) await refreshTemplateReads(outcome.scope);
  };

  return { remove: handleDeleteItem, loading };
};

// Edit and delete for one custom template (classic Templates page).
export const TemplateItemActions = ({
  template,
}: {
  template: Template;
}): JSX.Element => {
  const { remove: handleDeleteItem, loading } = useTemplateDelete();
  return (
    <div className="flex shrink-0 items-center">
      <Link
        aria-label={t("templates.home.edit")}
        className="flex size-11 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring lg:size-9"
        params={{ templatePublicId: "" + template.public_id }}
        title={t("templates.home.edit")}
        to="/template/$templatePublicId"
      >
        <Pencil aria-hidden className="size-[18px]" />
      </Link>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <button
            aria-label={t("templates.home.delete")}
            className="flex size-11 items-center justify-center rounded-md text-error hover:bg-error/10 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring lg:size-9"
            title={t("templates.home.delete")}
            type="button"
          >
            <Trash2 aria-hidden className="size-[18px]" />
          </button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("templates.deleteTemplateAlert.headerTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("templates.deleteTemplateAlert.deleteCTA")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              disabled={loading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/80"
              onClick={() => {
                void handleDeleteItem(template.public_id);
              }}
            >
              {t("templates.deleteTemplateAlert.deleteButton")}
            </AlertDialogAction>
            <AlertDialogCancel>
              {t("templates.deleteTemplateAlert.cancelButton")}
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const TemplateItem = ({
  template,
}: {
  template: Template;
}): JSX.Element => (
  <div className="flex items-center justify-between py-4 px-6">
    <div className="space-y-1">
      <h3 className="text-primary font-medium text-base">{template.name}</h3>
    </div>
    <TemplateItemActions template={template} />
  </div>
);
